<script setup>
    // Seite zum Verschieben vergangener Wiedervorlagen
    import { ref, reactive, onMounted, computed } from 'vue'
    import { storeToRefs } from "pinia";

    // TODO: allow for routing to the details page of the businessPartner
    import { useRoute } from 'vue-router'
    const route = useRoute()

    import { useRouter } from 'vue-router'

    import Reminder from '@/components/Reminder.vue'
    import userAvatar from '@/components/userAvatar.vue'
    import Spinner from '@/components/Spinner.vue'
    import PreviewHeader from '@/components/PreviewHeader.vue'

    import { useUserStore } from '@/stores/user'
    const userStore = useUserStore()
    const router = useRouter()

    import { useBusinessPartnersStore } from '@/stores/businessPartners'
    const businessPartnersStore = useBusinessPartnersStore()

    const isSaving = ref(false)
    const loading = ref(false)

    const handleKundendetailsClick = async (gpNo) => {
        loading.value = true
        try {
            await router.push({ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: gpNo } })
        } finally {
            loading.value = false
        }
    }
    const additionalProduct = ref(-1);
    const additionalProducts = reactive([]);
    const additionalProductForFollowUpAppointment = ref(-1);
    const additionalProductsForFollowUpAppointment = reactive([]);
    const feedback = reactive({})
    const feedbackList = businessPartnersStore.businessPartnerListBySignature(`${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:VorOrtTermine`)
    const businessPartnersWithoutFeedback = computed(() => {
        return feedbackList.filter((businessPartner) => businessPartner.hasFeedback === false)
    })
    const selectedBusinessPartner = computed(() => {
        return feedbackList.find((businessPartner) => businessPartner.selected === true)
    })

    const saveDisabled = computed(() => {
        return !feedback.isValid || userStore.currentSelection.scope!=='salesAgent'
    })

    // ─── Termin-Datum (Issue: Datum sichtbar machen) ────────────────────────────
    // formatiert als "Termin vom DD.MM.YYYY" (+ ", HH:mm Uhr" falls eine Uhrzeit
    // vorhanden ist). Liest aus businessPartner.terminDate statt salesPitch.dateFollowUp,
    // da handleSelectBusinessPartner() dateFollowUp beim Öffnen bewusst auf null setzt
    // (siehe unten) — der ursprüngliche Termin würde sonst beim Aufklappen verschwinden.
    function formatTerminDatum(dateTimeString) {
        if (!dateTimeString) return ''
        const date = new Date(dateTimeString)
        if (isNaN(date.getTime())) return ''
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = date.getHours()
        const minutes = date.getMinutes()
        const hasTime = hours !== 0 || minutes !== 0
        const label = `Termin vom ${day}.${month}.${year}`
        return hasTime ? `${label}, ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} Uhr` : label
    }

    // ─── Umsatz-Pflichtfeld bei Abschluss (Issue) ───────────────────────────────
    const salesValueRequired = computed(() => {
        return feedback.businessPartner?.salesPitch?.offerList?.some((offer) => offer.status.name === 'Abschluss') ?? false
    })
    const salesValueMissing = computed(() => {
        if (!salesValueRequired.value || !feedback.businessPartner) return false
        const businessPartner = feedbackList.find((bp) => bp.id === feedback.businessPartner.id)
        return !(Number(businessPartner?.salesValue) > 0)
    })

    const selectableProducts = computed(() => {
        const alreadySelectedProducts = []
        const selectedBusinessPartner = feedbackList.find((businessPartner) => businessPartner.selected === true)
        if (selectedBusinessPartner) alreadySelectedProducts.push(...selectedBusinessPartner?.offerList.map((offer) => offer.product.id))
        // console.log(alreadySelectedProducts)
        alreadySelectedProducts.push(...additionalProducts.map((product) => product.id))
        // console.log(alreadySelectedProducts)
        const unselectedProducts = userStore.filterOption.businessPartnerPotential.filter((product) => {
            const raw = product?.id;
            if (raw === -1) return false;
            const idNum = typeof raw === 'string'
                ? parseInt(raw.split(':')[1])
                : (typeof raw === 'number' ? raw : NaN);
            return !Number.isNaN(idNum) && !alreadySelectedProducts.includes(idNum);
        })
        // console.log(unselectedProducts)
        return unselectedProducts
        // return (userStore.filterOption) ? userStore.filterOption.businessPartnerPotential.slice(1).filter((product) => !alreadySelectedProducts.includes(product.id)) : []
    })

    const selectableProductsForFollowUpAppointment = computed(() => {
        const alreadySelectedProducts = []
        const selectedBusinessPartner = feedbackList.find((businessPartner) => businessPartner.selected === true)
        // if (selectedBusinessPartner) alreadySelectedProducts.push(...selectedBusinessPartner?.offerList.map((offer) => offer.product.id))
        alreadySelectedProducts.push(...additionalProductsForFollowUpAppointment.map((product) => product.id))
        // console.log(alreadySelectedProducts)
        const unselectedProducts = userStore.filterOption.businessPartnerPotential.filter((product) => {
            const raw = product?.id;
            if (raw === -1) return false;
            const idNum = typeof raw === 'string'
                ? parseInt(raw.split(':')[1])
                : (typeof raw === 'number' ? raw : NaN);
            return !Number.isNaN(idNum) && !alreadySelectedProducts.includes(idNum);
        })
        // console.log(unselectedProducts)
        return unselectedProducts
        // return (userStore.filterOption) ? userStore.filterOption.businessPartnerPotential.slice(1).filter((product) => !alreadySelectedProducts.includes(product.id)) : []
    })

    onMounted(() => {
        for (const businessPartner of feedbackList) {
            // terminDate: Schnappschuss VOR handleSelectBusinessPartner(), das
            // salesPitch.dateFollowUp gleich wieder auf null setzt.
            Object.assign(businessPartner, { followUpAppointmentOfferList: [], followUpAppointmentPotentals: [], showFollowUpAppointment: false, followUpAppointment: false, hasFeedback: false, selected: false, feedback: null, salesValue: null, terminDate: businessPartner.salesPitch?.dateFollowUp || null })
        }
        // open up the first businessPartner for feedback (guard: nothing to select if the agent has no pending items)
        if (feedbackList.length > 0) handleSelectBusinessPartner(feedbackList[0].id);
        // console.log('businessPartners without Feedback', businessPartnersWithoutFeedback)
    })

    const handleSelectBusinessPartner = (id) => {
        // console.log('selecting businessPartner', id)
        // console.log('feedbackList', feedbackList)
        // if the already selected businessPartner is clicked again, deselect it
        const selectedBusinessPartner = feedbackList.find((businessPartner) => businessPartner.selected === true)
        if (selectedBusinessPartner?.id===id) {
            selectedBusinessPartner.selected = false;
            return false
        }
        // if another businessPartner selected, open it and deselect all others
        for (const businessPartner of feedbackList) { Object.assign(businessPartner, { selected: (businessPartner.id === id) ? true : false }) }
        Object.assign(feedback, {
            isValid: false,
            displayAction: true,
            businessPartner: Object.assign({}, feedbackList.find((businessPartner) => businessPartner.id === id)),
            rescheduleMode: null,
            rescheduleDate: null,
            rescheduleTime: null,
            followUpAppointmentMode: null,
            followUpAppointmentDate: null,
            followUpAppointmentTime: null,
        })
        for (let offer of feedback.businessPartner.salesPitch.offerList) {
            Object.assign(offer, {
                status: {
                    name: 'Abschluss',
                    nameAdd: (feedback.businessPartner.salesPitch.status.id === 6) ? 'telefonisch' : 'vor Ort',
                    id: (feedback.businessPartner.salesPitch.status.id === 6) ? 20 : 19,
                    refusalReason: null,
                }, product: {
                    id: offer.id
                }
            });
        }
        Object.assign(feedback.businessPartner.salesPitch, { skipBusinessPartnerUpdate: true, dateFollowUp: null })
        handleCheckFeedback(id);
    }

    function handleAddProduct(id) {
        const businessPartner = feedbackList.find((businessPartner) => businessPartner.id === id)
        if(!businessPartner.showFollowUpAppointment) {
            const selectedProduct = userStore.filterOption.businessPartnerPotential.find((product) => product.id === additionalProduct.value);
            // console.log('handleAddProduct', selectedProduct);
            additionalProducts.push({ id: selectedProduct.id, shortcut: selectedProduct.shortcut, name: selectedProduct.name, selfCreated: true });
            additionalProduct.value = -1;
        } else {
            const selectedProduct = userStore.filterOption.businessPartnerPotential.find((product) => product.id === additionalProductForFollowUpAppointment.value);
            // console.log('handleAddProduct', selectedProduct);
            additionalProductsForFollowUpAppointment.push({ id: selectedProduct.id, shortcut: selectedProduct.shortcut, name: selectedProduct.name, selfCreated: true });
            additionalProductForFollowUpAppointment.value = -1;
            handleCheckFeedback(id);
        }
    }

    function handleRemoveAdditionalProductList(idProduct, idBusinessPartner){
        const businessPartner = feedbackList.find((businessPartner) => businessPartner.id === idBusinessPartner)
        if(!businessPartner.showFollowUpAppointment) {
            // const product = feedback.businessPartner.salesPitch.offerList.find((product) => product.id === id);
            additionalProducts.splice(additionalProducts.findIndex((product) => product.id === idProduct), 1);
        } else {
            additionalProductsForFollowUpAppointment.splice(additionalProductsForFollowUpAppointment.findIndex((product) => product.id === idProduct), 1);
            handleCheckFeedback(idBusinessPartner);
        }
    }

    function handleRemoveAdditionalProduct(id) {
        // console.log('handleRemoveAdditionalProduct', id);
        const product = feedback.businessPartner.salesPitch.offerList.find((product) => product.id === id);
        // console.log('product', product.status.selfCreated);
        feedback.businessPartner.salesPitch.offerList.splice(feedback.businessPartner.salesPitch.offerList.findIndex((product) => product.id === id), 1);
    }

    function handleAddAllProducts(id) {
        // console.log('handleAddProduct', additionalProducts);
        // const selectedBusinessPartner = feedbackList.find((businessPartner) => businessPartner.selected === true)
        const businessPartner = feedbackList.find((businessPartner) => businessPartner.id === id)
        if(!businessPartner.showFollowUpAppointment) {
            for (const product of additionalProducts) {
                const rawId = product?.id;
                const idNum = typeof rawId === 'string' ? parseInt(rawId.replaceAll('product:', '')) : (typeof rawId === 'number' ? rawId : NaN);
                if (Number.isNaN(idNum)) continue;
                businessPartner.offerList.push({   
                    id: idNum, 
                    shortcut: product.shortcut, 
                    name: product.name,
                    status: {
                        name: 'Abschluss',
                        nameAdd: (feedback.businessPartner.salesPitch.status.id === 6) ? 'telefonisch' : 'vor Ort',
                        id: (feedback.businessPartner.salesPitch.status.id === 6) ? 20 : 19,
                        refusalReason: null,
                        selfCreated: true
                    }, product: {
                        id: idNum
                    }
                });
            }
            // empty the additionalProducts array
            additionalProducts.length = 0;
            // die neuen Potentiale sind als Abschluss vorbelegt: Speichern-Button sofort neu bewerten
            handleCheckFeedback(id);
        } else {
            // console.log('add folgetermin potential')
            for (const product of additionalProductsForFollowUpAppointment) {
                const rawId = product?.id;
                const idNum = typeof rawId === 'string' ? parseInt(rawId.replaceAll('product:', '')) : (typeof rawId === 'number' ? rawId : NaN);
                if (Number.isNaN(idNum)) continue;
                businessPartner.followUpAppointmentOfferList.push({   
                    id: idNum, 
                    shortcut: product.shortcut, 
                    name: product.name,
                    status: {
                        name: 'Abschluss',
                        nameAdd: (feedback.businessPartner.salesPitch.status.id === 6) ? 'telefonisch' : 'vor Ort',
                        id: (feedback.businessPartner.salesPitch.status.id === 6) ? 20 : 19,
                        refusalReason: null,
                        selfCreated: true
                    }, product: {
                        id: idNum
                    }
                });
            }
            // empty the additionalProducts array
            additionalProductsForFollowUpAppointment.length = 0;
        }
    }

    const handleCheckFeedback = (id) => {
        const businessPartner = feedbackList.find((businessPartner) => businessPartner.id === id)
        // console.log(feedback)
        // console.log('Start', feedback.isValid, feedback.followUpAppointmentMode)
        feedback.isValid = false;
        if(!businessPartner.showFollowUpAppointment) {
            // console.log('checking feedback', feedback.displayAction)
            if (feedback.displayAction === false) {
                // console.log('feedback #2', feedback.displayAction)
                if (feedback.rescheduleMode == 2) {
                    // console.log('feedback #3', feedback.displayAction)
                    // rescheduleMode 2: Wiedervorlage only needs a date
                    feedback.isValid = (feedback.rescheduleDate) ? true : false;
                } else if (feedback.rescheduleMode) {
                    // rescheduleMode 6/7: verbindlicher Termin tel/vor Ort needs date and time
                    feedback.isValid = (feedback.rescheduleDate && feedback.rescheduleTime) ? true : false;
                }
                if (feedback.isValid) {
                    feedback.businessPartner.salesPitch.dateFollowUp = `${feedback.rescheduleDate}T${(feedback.rescheduleTime) ? feedback.rescheduleTime : '00:00'}:00.000Z`
                    // on rescheduling all offers need to have the status 23: Terminiert again
                    for (const offer of feedback.businessPartner.salesPitch.offerList) Object.assign(offer.status, { id: 23, name: 'Terminiert', refusalReason: null })
                }
            } else if (feedback.displayAction === true) {
                // console.log('display action is', feedback.displayAction, 'salesPitch', feedback.businessPartner.salesPitch)
                for (const offer of feedback.businessPartner.salesPitch.offerList) {
                    if (['Abschluss', 'nicht Angesprochen', 'Interesse (Folgekampagne)'].includes(offer.status.name)) feedback.isValid = (offer.status.id) ? true : false;
                    if (offer.status.name === 'Abgelehnt') feedback.isValid = (offer.refusalReason) ? true : false;
                    // set the corect status id based on the name
                    switch (offer.status.name) {
                        case 'Abschluss':
                            offer.status.id = (feedback.businessPartner.salesPitch.status.id === 6) ? 20 : 19;
                            break;
                        case 'Abgelehnt':
                            offer.status.id = 21;
                            break;
                        case 'nicht Angesprochen':
                            offer.status.id = 28;
                            break;
                        case 'Interesse (Folgekampagne)':
                            offer.status.id = 18;
                            break;
                        default:
                            break;
                    }
                    // console.log('managed offer', offer.name, 'status', offer.status.name, offer.status.id, offer.refusalReason)
                    
                    if (feedback.isValid === false) return false;
                }
                // Umsatz ist Pflicht, sobald mindestens ein Potenzial als "Abschluss" markiert ist —
                // ohne Umsatz darf in diesem Fall nicht gespeichert werden.
                const hasAbschluss = feedback.businessPartner.salesPitch.offerList.some((offer) => offer.status.name === 'Abschluss');
                if (hasAbschluss && !(Number(businessPartner.salesValue) > 0)) {
                    feedback.isValid = false;
                    return false;
                }
            }
            // console.log('reschedule, mode is', feedback.rescheduleMode, 'isValid', feedback.isValid, 'salesPitch', feedback.businessPartner.salesPitch)
            return true;
        } else {
            // console.log('feedback #2', feedback.displayAction)
            if (feedback.followUpAppointmentMode) {
                // followUpAppointmentMode 6/7: verbindlicher Termin tel/vor Ort needs date and time
                feedback.isValid = (feedback.followUpAppointmentDate && feedback.followUpAppointmentTime && additionalProductsForFollowUpAppointment.length > 0) ? true : false;
            }
            if (feedback.isValid) feedback.businessPartner.salesPitch.dateFollowUp = `${feedback.followUpAppointmentDate}T${(feedback.followUpAppointmentTime) ? feedback.followUpAppointmentTime : '00:00'}:00.000Z`
        }
        // console.log('End', feedback.isValid)
    }

    const handleSaveFeedback = async (id) => {
        const businessPartner = feedbackList.find((businessPartner) => businessPartner.id === id)
        // Wenn Kunde einen Folgetermin möchte, setze showFollowUpAppointment explizit
        if (businessPartner && businessPartner.followUpAppointment) {
            businessPartner.showFollowUpAppointment = true;
        }
        console.debug('[DEBUG] handleSaveFeedback called for id:', id);
        const debugBP = feedbackList.find((bp) => bp.id === id);
        console.debug('[DEBUG] businessPartner before save:', JSON.parse(JSON.stringify(debugBP)));
        if (!businessPartner || handleCheckFeedback(id) === false) return false
        // make sure that any old salesPitch is removed
        delete businessPartner.salesPitch
        let salesPitch = {}
        
        if (!businessPartner.showFollowUpAppointment) {
            // build a salesPitch from regular feedback (not the followUpAppointment)
            // Always use contact from feedback.businessPartner.salesPitch
            const contact = feedback.businessPartner.salesPitch && feedback.businessPartner.salesPitch.contact ? feedback.businessPartner.salesPitch.contact : businessPartner.contact;
            salesPitch = {
                id: null,
                skipBusinessPartnerUpdate: true,
                status: (feedback.displayAction) ?  {id : 22} : { id: parseInt(feedback.rescheduleMode)},
                dateFollowUp: (feedback.displayAction) ? null : (feedback.businessPartner.salesPitch ? feedback.businessPartner.salesPitch.dateFollowUp : null),
                contact: contact,
                offerList: feedback.businessPartner.salesPitch ? feedback.businessPartner.salesPitch.offerList : [],
                salesValue: businessPartner.salesValue ?? null,
            }
        } else {
            console.debug('[DEBUG] FollowUpAppointment detected, keeping customer open.');
            // build a salesPitch from the followUpAppointment data
            // Always use contact from feedback.businessPartner.salesPitch
            const contact = feedback.businessPartner.salesPitch && feedback.businessPartner.salesPitch.contact ? feedback.businessPartner.salesPitch.contact : businessPartner.contact;
            salesPitch = {
                id: null,
                isFollowUpAppointment: true,
                skipBusinessPartnerUpdate: true,
                status: { id: parseInt(feedback.followUpAppointmentMode)},
                dateFollowUp: feedback.businessPartner.salesPitch ? feedback.businessPartner.salesPitch.dateFollowUp : null,
                comment: feedback.businessPartner.salesPitch ? feedback.businessPartner.salesPitch.comment : null,
                contact: contact,
                offerList: [],
                salesValue: businessPartner.salesValue ?? null,
            }
            // fill the offers from additionalProductsForFollowUpAppointment with status 'terminiert'
            for (const product of additionalProductsForFollowUpAppointment) {
                const rawId = product?.id;
                const idNum = typeof rawId === 'string' ? parseInt(rawId.replaceAll('product:', '')) : (typeof rawId === 'number' ? rawId : NaN);
                if (Number.isNaN(idNum)) continue;
                salesPitch.offerList.push({
                    id: idNum, 
                    shortcut: product.shortcut, 
                    name: product.name,
                    status: { 
                        name: 'Terminiert',
                        id: 23,  
                        refusalReason: null,
                        selfCreated: true
                    }, product: {
                        id: idNum
                    }
                })
            }
        }
        Object.assign(businessPartner, { salesPitch: salesPitch })
        businessPartner.salesPitchList = [businessPartner.salesPitch]
        // Set all counters to -1
        feedbackList.forEach(bp => { bp.count = -1 })
        // Remove the current customer from the feedbackList ONLY if not follow-up appointment
        if (!businessPartner.showFollowUpAppointment) {
            const idx = feedbackList.findIndex(bp => bp.id === id)
            if (idx !== -1) feedbackList.splice(idx, 1)
        } else {
            // Ensure the customer stays open and the follow-up form is shown
            businessPartner.showFollowUpAppointment = true;
            businessPartner.selected = true;
        }
        isSaving.value = true
        const saveFeedbackSuccess = await businessPartnersStore.saveFeedback(businessPartner)
        await userStore.refreshAppointments()
        isSaving.value = false
        console.debug('[DEBUG] After save, businessPartner:', JSON.parse(JSON.stringify(businessPartner)));
        console.debug('[DEBUG] feedbackList after save:', JSON.parse(JSON.stringify(feedbackList)));
        // Nach dem Speichern: Wenn Folgetermin aktiv, bleibe beim aktuellen Kunden, sonst zum nächsten Kunden
        console.debug('[DEBUG] feedbackList.length:', feedbackList.length, 'showFollowUpAppointment:', businessPartner.showFollowUpAppointment);
        if (feedbackList.length === 0) {
            router.push({ name: 'Dashboard', hash: '#potential-bk' })
        } else if (businessPartner.showFollowUpAppointment) {
            // Bleibe beim aktuellen Kunden, damit Folgetermin ausgefüllt werden kann
            // Do not re-select, just keep the form open
        } else {
            // Zum nächsten Kunden wechseln
            handleSelectBusinessPartner(feedbackList[0].id)
        }
        return true
    }

    const handleAbortFeedback = (id) => {
        // console.log('hanuta4')
        // on abort, mark the businessPartner as having feedback
        feedbackList.find((businessPartner) => businessPartner.id === id).hasFeedback = true

        
        if (feedbackList.filter((businessPartner) => businessPartner.hasFeedback === false).length === 0) {
            router.push({ name: 'Dashboard', hash: '#potential-bk' })
        } else {
            handleSelectBusinessPartner(feedbackList.find((businessPartner) => businessPartner.hasFeedback === false).id)
        }
        
        return true
    }
</script>

<template>
    <div class="reminder-wrapper singlepage-wrapper max-width">
        <Spinner v-if="loading" text="wird geöffnet..." />
        <Spinner v-if="isSaving" text="wird gespeichert..." />
        <main id="container-main">
            <preview-header
                class="feedback-preview-header"
                title="Feedback"
                :summary="`${businessPartnersWithoutFeedback.length} Kunden`"
                :to="{ name: 'Dashboard', hash: '#potential-bk' }"
                button-label="Zurück zum Dashboard"
                arrow="left"
            />
            <div>
                <div class="widget-wrapper potential-bk feedback">
                    <div class="container-wrapper scroll-y">
                        <div :class="['customer', { 'selected': businessPartner.selected }]" v-for="(businessPartner, index) in businessPartnersWithoutFeedback" :key="businessPartner.id">
                            <span v-if="index < 9" class="businessPartnerIndex">0{{ index + 1 }}</span>
                            <span v-else class="businessPartnerIndex">{{ index + 1 }}</span>
                            <div class="informations-wrapper">
                                <div class="accordion-menu notes-details">
                                    <ul class="main-list">
                                        <userAvatar v-if="userStore.currentSelection.scope!=='salesAgent'" :showSalesAgent=true :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback" :salesAgentId="businessPartner.idSalesAgent" />
                                        <li class="main-list-item">
                                            <h3 class="feedback-header" @click="handleSelectBusinessPartner(businessPartner.id)">
                                                <span class="txt-overflow feedback-header__name">{{ businessPartner.name }}</span>
                                                <span v-if="formatTerminDatum(businessPartner.terminDate)" class="termin-datum feedback-header__meta">· {{ formatTerminDatum(businessPartner.terminDate) }}</span>
                                                <div class="pill feedback-header__meta">GPNo: [{{ businessPartner.gpNo }}]</div>
                                                <i class="arrow"></i>
                                            </h3>
                                            <!-- <div>{{ businessPartner.salesAgent }}</div> -->
                                            <!-- <userAvatar v-if="userStore.currentSelection.scope!=='salesAgent'" :showSalesAgent=true :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback" :salesAgentId="businessPartner.salesAgentId" /> -->
                                            <div class="accordion-content-wrapper">
                                                <div class="accordion-content">
                                                    <div class="nav">
                                                        <div :class="['input-wrapper toggle']">
                                                            <span :class="['switcher switcher-1', {'FollowUpAppointment' : businessPartner.showFollowUpAppointment}]">
                                                                <input type="checkbox" id="switcher-1" class="move"
                                                                    @change="handleCheckFeedback(businessPartner.id)"
                                                                    v-model="feedback.displayAction">
                                                                <label for="switcher-1"></label>
                                                            </span>
                                                        </div>
                                                        <button v-if="!businessPartner.showFollowUpAppointment" @click="handleKundendetailsClick(businessPartner.gpNo)"><div><span>Kundendetails</span></div></button>
                                                    </div>
                                                    <p v-if="feedback.followUpAppointmentMode && businessPartner.showFollowUpAppointment && additionalProductsForFollowUpAppointment.length === 0 && feedback.followUpAppointmentDate && feedback.followUpAppointmentTime" class="warning-txt">Bitte mindestens 1 Potential auswählen!</p>
                                                    <!-- <p v-else-if="feedback.followUpAppointmentMode && businessPartner.showFollowUpAppointment && (!feedback.followUpAppointmentDate || !feedback.followUpAppointmentTime)" class="warning-txt">Bitte Datum & Uhrzeit auswählen!</p>
                                                    <p v-else-if="businessPartner.showFollowUpAppointment" class="warning-txt">Bitte Terminart treffen!</p> -->
                                                    <!-- Spinner für Speichern wird global angezeigt -->
                                                    <template v-if="!businessPartner.showFollowUpAppointment">
                                                        <div :class="['follow-up-appointment container', {'cell-grid disabled' : saveDisabled}]" v-if="new Date() <= new Date(userStore.currentSelection.campaign.dateEnd)">
                                                            <input type="checkbox" id="follow-up-appointment" v-model="businessPartner.followUpAppointment" disabled>
                                                            <label for="follow-up-appointment">Kunde möchte Folgetermin</label>
                                                            <span class="follow-up-disabled-text">(Kommt demnächst)</span>
                                                        </div>
                                                        <div id="warning" class="container negative follow-up-appointment" v-else>Kein Folgetermin möglich – Kampagne ist abgelaufen.</div>
                                                        <div class="sum container umsatz-modern" :class="{ 'umsatz-modern--required': salesValueRequired, 'umsatz-modern--missing': salesValueMissing }">
                                                            <div class="umsatz-modern__row">
                                                                <label for="umsatz-input">Umsatz<span v-if="salesValueRequired" class="umsatz-required-star" title="Pflichtfeld bei Abschluss">&nbsp;*</span>&nbsp;<span class="umsatz-euro">€</span></label>
                                                                <input id="umsatz-input" type="number" min="0" step="0.01" v-model="businessPartner.salesValue" placeholder="0,00" @input="handleCheckFeedback(businessPartner.id)" />
                                                            </div>
                                                            <p v-if="salesValueMissing" class="warning-txt umsatz-warning">Bitte gib den Umsatz an — bei einem Abschluss ist das Feld Pflicht.</p>
                                                        </div>

                                                        <div class="informations-wrapper">
                                                            <div v-if="[true].includes(feedback.displayAction)" class="input-wrapper feedback list">
                                                                <div class="anchor" :id="'_' + index"></div>
                                                                <div :class="['potential-wrapper container', {'selfCreated' : offer.status.selfCreated}]"
                                                                    v-for="(offer, feedbackIndex) in feedback.businessPartner.salesPitch.offerList"
                                                                    :key="offer.id">
                                                                    <div class="label-wrapper">
                                                                        <span class="label new">Neu</span>
                                                                        <span class="label delete" @click="handleRemoveAdditionalProduct(offer.id)">Entfernen</span>
                                                                    </div>
                                                                    <div class="headline">
                                                                        <h4 class="status-heading product left">{{ offer.name }}</h4>
                                                                        <span class="index">{{ feedbackIndex + 1 + "/" + feedback.businessPartner.salesPitch.offerList.length }}</span>
                                                                    </div>
                                                                    <div class="inputs">
                                                                        <div class="single-input">
                                                                            <input type="radio" selected id="Abschluss" @change="handleCheckFeedback(feedback.businessPartner.id)" value="Abschluss" v-model="offer.status.name">
                                                                            <label for="Abschluss">Abschluss {{ offer.status.nameAdd}}</label>
                                                                        </div>
                                                                        <div class="single-input">
                                                                            <input type="radio" id="Abgelehnt" @change="handleCheckFeedback(feedback.businessPartner.id)" value="Abgelehnt" v-model="offer.status.name">
                                                                            <label for="Abgelehnt">Abgelehnt</label>
                                                                        </div>
                                                                        <div class="submenu">
                                                                            <div class="single-input" v-if="offer.status.name == 'Abgelehnt'">
                                                                                <input type="radio" selected id="kein Interesse" value="kein Interesse" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="offer.refusalReason">
                                                                                <label for="kein Interesse">kein Interesse</label>
                                                                            </div>
                                                                            <div class="single-input" v-if="offer.status.name == 'Abgelehnt'">
                                                                                <input type="radio" selected id="anderer Anbieter" value="anderer Anbieter" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="offer.refusalReason">
                                                                                <label for="anderer Anbieter">anderer Anbieter</label>
                                                                            </div>
                                                                            <div class="single-input" v-if="offer.status.name == 'Abgelehnt'">
                                                                                <input type="radio" selected id="Inhouse" value="Inhouse" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="offer.refusalReason">
                                                                                <label for="Inhouse">Inhouse</label>
                                                                            </div>
                                                                        </div>
                                                                        <div v-if="!offer.status.selfCreated" class="single-input">
                                                                            <input type="radio" id="nicht Angesprochen" @change="handleCheckFeedback(feedback.businessPartner.id)" value="nicht Angesprochen" v-model="offer.status.name">
                                                                            <label for="nicht Angesprochen">Nicht Angesprochen</label>
                                                                        </div>
                                                                        <div class="single-input">
                                                                            <input type="radio" id="Interesse (Folgekampagne)" @change="handleCheckFeedback(feedback.businessPartner.id)" value="Interesse (Folgekampagne)" v-model="offer.status.name">
                                                                            <label for="Interesse (Folgekampagne)">Interesse (Folgekampagne)</label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="potential-wrapper container add">
                                                                    <div class="headline">
                                                                        <h4 class="status-heading plus left">Potential hinzufügen</h4>
                                                                    </div>
                                                                    <div class="potentiale-pending">
                                                                        <div class="info-wrapper container" v-for="product in additionalProducts" :key="product.id">
                                                                            <div class="select-wrapper">
                                                                                <p :title="'Potential'" class="_16">{{ product.name }}</p>
                                                                                <button @click="handleRemoveAdditionalProductList(product.id, businessPartner.id)"></button>
                                                                            </div>
                                                                        </div>
                                                                        <div class="add-potential-wrapper container">
                                                                            <select v-model="additionalProduct">
                                                                                <option disabled value="-1" selected>Weiteres Produkt hinzufügen</option>
                                                                                <option v-for="product in selectableProducts" :key="product.id" :value="product.id">
                                                                                    {{ product.name }}
                                                                                </option>
                                                                            </select>
                                                                            <button @click="handleAddProduct(feedback.businessPartner.id)" :disabled="additionalProduct===-1" class="add-potential round"></button>
                                                                        </div>
                                                                        <button @click="handleAddAllProducts(feedback.businessPartner.id)" class="btn-add-potentials">Potentiale hinzufügen</button>

                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div v-if="[false].includes(feedback.displayAction)"
                                                                class="input-wrapper feedback list">
                                                                <div class="potential-wrapper container">
                                                                    <div class="headline">
                                                                        <h4 class="status-heading reschedule left">Verschieben auf:</h4>
                                                                    </div>
                                                                    <div class="inputs">
                                                                        <div class="single-input">
                                                                            <input type="radio" id="rescheduleVorOrt" value="7" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.rescheduleMode">
                                                                            <label for="rescheduleVorOrt">verbindlicher vor Ort Termin</label>
                                                                        </div>
                                                                        <div class="single-input">
                                                                            <input type="radio" id="rescheduleTel" value="6" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.rescheduleMode">
                                                                            <label for="rescheduleTel">verbindlicher tel. Termin</label>
                                                                        </div>
                                                                        <div class="single-input">
                                                                            <input type="radio" id="rescheduleWVL" value="2" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.rescheduleMode">
                                                                            <label for="rescheduleWVL">nur Wiedervorlage</label>
                                                                        </div>
                                                                    </div>
                                                                    <div class="top-row">
                                                                        <input @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.rescheduleDate" class="btn" type="date" required>
                                                                        <input @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.rescheduleTime" class="btn" type="time" id="appt-time" name="appt-time" min="06:30" max="22:00" :required="feedback.rescheduleMode !== 2" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </template>
                                                    <template v-else>
                                                        <div class="informations-wrapper">
                                                            <div class="input-wrapper feedback list">
                                                                <div class="anchor" :id="'_' + index"></div>
                                                                <div :class="['potential-wrapper container']">
                                                                    <div class="headline">
                                                                        <h4 class="status-heading reschedule left">Folgetermin am:</h4>
                                                                    </div>
                                                                    <div class="inputs">
                                                                        <div class="single-input">
                                                                            <input type="radio" id="rescheduleVorOrt" value="7" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.followUpAppointmentMode">
                                                                            <label for="rescheduleVorOrt">verbindlicher vor Ort Termin</label>
                                                                        </div>
                                                                        <div class="single-input">
                                                                            <input type="radio" id="rescheduleTel" value="6" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.followUpAppointmentMode">
                                                                            <label for="rescheduleTel">verbindlicher tel. Termin</label>
                                                                        </div>
                                                                        <!-- <div class="single-input">
                                                                            <input type="radio" id="rescheduleWVL" value="2" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.rescheduleMode">
                                                                            <label for="rescheduleWVL">doch nicht (lol)</label>
                                                                        </div> -->
                                                                    </div>
                                                                    <div class="top-row">
                                                                        <input @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.followUpAppointmentDate" class="btn" type="date" required>
                                                                        <input @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="feedback.followUpAppointmentTime" class="btn" type="time" id="appt-time" name="appt-time" min="06:30" max="22:00" :required="feedback.rescheduleMode !== 2" />
                                                                    </div>
                                                                </div> 
                                                                <div :class="['potential-wrapper container', {'selfCreated' : offer.status.selfCreated}]"
                                                                    v-for="(offer, followUpAppointmentOfferListIndex) in feedback.businessPartner?.followUpAppointmentOfferList"
                                                                    :key="offer.id">
                                                                    <div class="label-wrapper">
                                                                        <span class="label new">Neu</span>
                                                                        <span class="label delete" @click="handleRemoveAdditionalProduct(offer.id)">Entfernen</span>
                                                                    </div>
                                                                    <div class="headline">
                                                                        <h4 class="status-heading product left">{{ offer.name }}</h4>
                                                                        <span class="index">{{ followUpAppointmentOfferListIndex + 1 + "/" + feedback.businessPartner.salesPitch.offerList.length }}</span>
                                                                    </div>
                                                                    <div class="inputs">
                                                                        <div class="single-input">
                                                                            <input type="radio" selected id="Abschluss" @change="handleCheckFeedback(feedback.businessPartner.id)" value="Abschluss" v-model="offer.status.name">
                                                                            <label for="Abschluss">Abschluss {{ offer.status.nameAdd}}</label>
                                                                        </div>
                                                                        <div class="single-input">
                                                                            <input type="radio" id="Abgelehnt" @change="handleCheckFeedback(feedback.businessPartner.id)" value="Abgelehnt" v-model="offer.status.name">
                                                                            <label for="Abgelehnt">Abgelehnt</label>
                                                                        </div>
                                                                        <div class="submenu">
                                                                            <div class="single-input" v-if="offer.status.name == 'Abgelehnt'">
                                                                                <input type="radio" selected id="kein Interesse" value="kein Interesse" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="offer.refusalReason">
                                                                                <label for="kein Interesse">kein Interesse</label>
                                                                            </div>
                                                                            <div class="single-input" v-if="offer.status.name == 'Abgelehnt'">
                                                                                <input type="radio" selected id="anderer Anbieter" value="anderer Anbieter" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="offer.refusalReason">
                                                                                <label for="anderer Anbieter">anderer Anbieter</label>
                                                                            </div>
                                                                            <div class="single-input" v-if="offer.status.name == 'Abgelehnt'">
                                                                                <input type="radio" selected id="Inhouse" value="Inhouse" @change="handleCheckFeedback(feedback.businessPartner.id)" v-model="offer.refusalReason">
                                                                                <label for="Inhouse">Inhouse</label>
                                                                            </div>
                                                                        </div>
                                                                        <div class="single-input">
                                                                            <input type="radio" id="nicht Angesprochen" @change="handleCheckFeedback(feedback.businessPartner.id)" value="nicht Angesprochen" v-model="offer.status.name">
                                                                            <label for="nicht Angesprochen">Nicht Angesprochen</label>
                                                                        </div>
                                                                        <div class="single-input">
                                                                            <input type="radio" id="Interesse (Folgekampagne)" @change="handleCheckFeedback(feedback.businessPartner.id)" value="Interesse (Folgekampagne)" v-model="offer.status.name">
                                                                            <label for="Interesse (Folgekampagne)">Interesse (Folgekampagne)</label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="potential-wrapper container add">
                                                                    <div class="headline">
                                                                        <h4 class="status-heading plus left">Folgetermin Potential</h4>
                                                                    </div>
                                                                    <div class="potentiale-pending">
                                                                        <div class="info-wrapper container" v-for="product in additionalProductsForFollowUpAppointment" :key="product.id">
                                                                            <div class="select-wrapper">
                                                                                <p :title="'Potential'" class="_16">{{ product.name }}</p>
                                                                                <button @click="handleRemoveAdditionalProductList(product.id, businessPartner.id)"></button>
                                                                            </div>
                                                                        </div>
                                                                        <div class="add-potential-wrapper container">
                                                                            <select v-model="additionalProductForFollowUpAppointment">
                                                                                <option disabled value="-1" selected>Weiteres Produkt hinzufügen</option>
                                                                                <option v-for="product in selectableProductsForFollowUpAppointment" :key="product.id" :value="product.id">
                                                                                    {{ product.name }}
                                                                                </option>
                                                                            </select>
                                                                            <button @click="handleAddProduct(feedback.businessPartner.id)" :disabled="additionalProductForFollowUpAppointment===-1" class="add-potential round"></button>
                                                                        </div>
                                                                        <!-- <a :href="'#_' + index"><button @click="handleAddAllProducts(feedback.businessPartner.id)" class="btn-add-potentials">Potentiale hinzufügen</button></a> -->

                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <textarea class="container" v-model="feedback.businessPartner.salesPitch.comment" placeholder="Kommentar zum Folgetermin ?" />
                                                    </template>
                                                    <button class="cancel customerCancel" v-if="businessPartner.showFollowUpAppointment"><div><span>Abbrechen</span></div></button>
                                                    <button class="customerSave" :disabled="userStore.preventSave || saveDisabled" @click="handleSaveFeedback(businessPartner.id)">Speichern</button>
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
        <reminder></reminder>
    </div>
</template>

<style scope>
.umsatz-modern {
    /* Column now, not row: the validation message lives inside this box (see
       .umsatz-modern__row below) so it stays visually tied to the Umsatz
       field instead of being an unplaced sibling that CSS Grid auto-placement
       (accordion-content's grid-template-areas, salesPitch.scss) would sweep
       into the empty buttonCancel/buttonSave cells next to the nav toolbar. */
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
    /* background: var(--sellwerk-color-bg, #f7fafd); */
    background-color: rgba(234, 234, 234, 0.25);
    border: 1px solid var(--sellwerk-color-border, #e0e6ed);
    border-radius: 1.2em;
    padding: 0.35em 1em 0.35em 0.9em;
    box-shadow: 0 2px 8px -4px rgba(0,0,0,0.04);
    /* margin-left: 1.5rem; */
    font-size: 1em;
    /* Caps how wide this box gets: the "sum sum" grid track it sits in is
       ~2/3 of the (now full-width) card, and stretching the box to fill that
       whole track dragged the input far away from its label with a huge
       empty gap. 420px keeps it a compact, left-aligned card while still
       being able to shrink on narrower tracks (mobile single-column layout,
       where the track itself is under 420px anyway). */
    width: auto;
    max-width: 420px;
    /* Grid items default to a min-width of their max-content size, which lets
       this box push the "sum sum" grid track (salesPitch.scss) wider than the
       card and off-screen at some breakpoints. min-width: 0 lets it actually
       shrink to the track's real width instead of blowing it out. */
    min-width: 0;
    box-sizing: border-box;
}
.umsatz-modern__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.6rem;
    width: 100%;
}
.umsatz-modern label {
    font-weight: 600;
    color: var(--sellwerk-color-text, #223);
    margin-bottom: 0;
    letter-spacing: 0.01em;
    font-size: 1em;
    display: flex;
    align-items: center;
}
.umsatz-modern .umsatz-euro {
    font-size: 1em;
    color: var(--sellwerk-color-main-accent, #008fb3);
    font-weight: 700;
    margin-left: 0.1em;
}
.umsatz-modern input[type="number"] {
    width: 110px;
    max-width: 100%;
    border: none;
    /* background: var(--sellwerk-color-bg, #fff); */
    border-radius: 0.7em;
    padding: 0.45em 0.8em;
    font-size: 1.1em;
    color: var(--sellwerk-color-text, #223);
    box-shadow: 0 1px 4px -2px rgba(0,0,0,0.06);
    transition: border 0.18s, box-shadow 0.18s;
    outline: none;
}
.umsatz-modern input[type="number"]:focus {
    border: 1.5px solid var(--sellwerk-color-main-accent, #008fb3);
    box-shadow: 0 0 0 2px rgba(var(--sellwerk-color-main-accent-rgb,0,143,179),0.10);
}

/* Feedback ist eine dedizierte Single-Page, aber die Wurzel trägt sowohl
   .reminder-wrapper als auch .singlepage-wrapper (siehe Template). Dadurch
   erbt main#container-main das mehrspaltige Dashboard-Grid aus
   dashboard.scss (".reminder-wrapper main"), das sein einziges Kind (das
   Feedback-Widget) über potentialPreview.scss (".widget-wrapper.potential-bk
   { grid-area: potential-bk }") in die schmale "potential-bk"-Spalte
   dieses Grids presst — auf großen Screens bleibt der Großteil der Seite
   leer. Der ID-Selektor hier gewinnt gegen die klassenbasierte
   dashboard.scss-Regel bei jeder Breite (auch ohne die dortigen
   @media-Blöcke zu duplizieren); !important auf dem Widget selbst ist
   nötig, weil salesPitch.scss's tiefer verschachtelte
   ".singlepage-wrapper main#container-main .widget-wrapper:nth-of-type(1)
   .potential-bk.feedback"-Regel (grid-column: span 4) sonst wegen höherer
   Selektor-Spezifität gewinnen würde. Keine der beiden Dateien wird
   angefasst, da sie auch vom echten Dashboard genutzt werden.
   Dieses <style>-Tag ist NICHT Vue-scoped (das "scope"-Attribut ist kein
   gültiges Vue-Attribut), die Regeln landen also global im Bundle. Ohne
   :has(.widget-wrapper.feedback) hätte die erste Regel main#container-main
   auf JEDER Single-Page mit .reminder-wrapper.singlepage-wrapper getroffen
   (z.B. BusinessPartnerList.vue nutzt exakt dieselbe Wrapper-Struktur) und
   deren Grid/Filter-Layout zerstört — daher die Einschränkung. */
.reminder-wrapper.singlepage-wrapper main#container-main:has(.widget-wrapper.feedback) {
    display: block;
    grid-template-columns: unset;
    grid-template-areas: unset;
}
.singlepage-wrapper main#container-main .widget-wrapper.feedback {
    grid-area: unset !important;
    grid-column: unset !important;
    width: 100% !important;
    max-width: 100% !important;
}

/* Kunden-Header (Name + Termin-Datum + GPNo-Pill): auf schmalen/mobilen
   Breiten fraß .txt-overflow (globals.scss) auf dem GANZEN <h3> Datum und
   GPNo-Pill komplett weg, sobald der Name allein schon die Zeile füllte —
   white-space:nowrap+overflow:hidden zusammen mit dem Feld salesPitch.scss
   ".feedback .../h3 { display:block }" (spezifischer als globals.scss'
   eigenes display:flex, gewinnt daher). Jetzt trunkiert nur noch der Name
   selbst; Datum/Pill/Pfeil bleiben immer sichtbar und brechen bei Bedarf in
   eine zweite Zeile um, statt abgeschnitten zu werden. !important nötig, um
   die spezifischere salesPitch.scss-Regel zu überstimmen, ohne diese
   (projektweit genutzte) Datei anzufassen.
   position:relative bleibt über die globals.scss-Basisregel erhalten, .arrow
   bleibt also korrekt relativ zu diesem <h3> positioniert. */
.widget-wrapper.feedback .main-list-item h3.feedback-header {
    display: flex !important;
    align-items: baseline;
    flex-wrap: wrap;
    row-gap: 0.15rem;
    column-gap: 0.4em;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
}
.feedback-header__name {
    min-width: 0;
    flex-shrink: 1;
    max-width: 100%;
}
.feedback-header__meta {
    flex-shrink: 0;
}

/* Termin-Datum neben dem Kundennamen */
.termin-datum {
    font-weight: 500;
    font-size: 0.85em;
    color: var(--sellwerk-color-text-muted, #6b7280);
}

/* "checkbox sum sum"-Zeile (salesPitch.scss, ab 540px): Spalte 1 ist "auto"
   und richtet sich nach dem Intrinsic-Content von .nav UND .follow-up-
   appointment — der lange Text "Kunde möchte Folgetermin (Kommt demnächst)"
   zog Spalte 1 auf ~70% der Breite und drängte die Umsatz-Box (Spalten 2+3)
   in einen schmalen Rest, in dem ihr max-content-Warntext hässlich umbrach.
   !important nötig gegen salesPitch.scss's tief verschachtelte, deutlich
   spezifischere Regel (".singlepage-wrapper main#container-main
   .widget-wrapper:nth-of-type(1).potential-bk.feedback .accordion-menu
   .main-list .main-list-item .accordion-content-wrapper .accordion-content"),
   ohne die (auch vom Dashboard-Preview genutzte) Datei anzufassen. */
@media only screen and (min-width: 540px) {
    .widget-wrapper.feedback .accordion-content {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) !important;
    }
}
.widget-wrapper.feedback .follow-up-appointment {
    flex-wrap: wrap;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
}

/* Umsatz-Pflichtfeld bei Abschluss */
.umsatz-required-star {
    color: rgba(var(--sellwerk-color-failure-rgb, 220, 38, 38), 1);
    font-weight: 700;
}
.umsatz-modern--required {
    border-color: var(--sellwerk-color-main-accent, #008fb3);
}
.umsatz-modern--missing {
    border-color: rgba(220, 38, 38, 0.6);
    background-color: rgba(220, 38, 38, 0.06);
}
.umsatz-modern--missing input[type="number"] {
    box-shadow: 0 0 0 1.5px rgba(220, 38, 38, 0.5);
}
.umsatz-warning {
    /* Now a flex child inside .umsatz-modern (column layout) — its own gap
       already spaces this from the label/input row above, and it needs to
       wrap normally instead of inheriting the pill's centered/nowrap text.
       Also dials back the global .warning-txt look (bold red, padded,
       shaking icon) which reads as a blocking error for what is just an
       inline field hint here. */
    font-size: 0.8em !important;
    font-weight: 400 !important;
    white-space: normal;
    text-align: left;
    padding: 0 !important;
    color: var(--sellwerk-color-text-muted, #6b7280) !important;
}
.umsatz-warning::before {
    content: none !important;
    animation: none !important;
    display: none !important;
}

/* Titel + Summary-Pill nebeneinander statt untereinander: PreviewHeader.vue's
   .ph-title-wrap ist standardmäßig flex-direction:column (Titel über der
   Pille). "feedback-preview-header" ist eine Klasse direkt am <preview-header
   class="feedback-preview-header">-Tag im Template — Vue reicht sie als
   Fallthrough-Attribut an dessen Root-Element weiter (gemerged mit
   PreviewHeader.vue's eigenem :class="headerClass"), was einen robusten,
   eindeutigen Hook ohne Abhängigkeit von Ahnen-Selektoren gibt.
   !important nötig gegen PreviewHeader.vue's scoped [data-v-xxxx]-Selektor. */
.feedback-preview-header .ph-title-wrap {
    flex-direction: row !important;
    align-items: center !important;
    gap: 0.6rem !important;
}
.feedback-preview-header .ph-summary {
    /* War width:100% relativ zu .ph-title-wrap — im Reihen-Modus würde das
       die Pille auf die volle Restbreite neben dem Titel strecken statt sie
       an ihren eigenen Text anzupassen. */
    width: auto !important;
}
/* main ist für diese Seite display:block (siehe main#container-main:has(...)
   -Regel oben) statt eines Flex/Grid-Layouts mit eigenem "gap" — ohne diesen
   margin-bottom stößt die Pille direkt an den Rahmen der Feedback-Karte
   darunter. */
.feedback-preview-header {
    margin-bottom: 1rem;
}
</style>